import {
  legacy,
  QAlert,
  QBox,
  QButton,
  QCloseButton,
  QCollapse,
  QHeading,
  QIconButton,
  QSpinner,
  QStack,
  QText,
  useToastProvider,
} from '@qualio/ui-components';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QualioDocument } from '../../../../api/model/document';
import {
  ChangeControlDTO,
  SuggestedChangeControlSection,
} from '../../../../types/DocumentCreate';
import { DocumentOverviewContext } from '../../DocumentOverview/Context';
import { aiService } from '../ai-service';
import styles from './GenerateCC.module.css';

import { Editor } from 'ckeditor5';
import { Parser } from 'html-to-react';

interface SuggestedSectionsProps {
  suggestedSections: SuggestedChangeControlSection[];
}

const htmlToReactParser = new Parser();

const SuggestedSections: React.FC<SuggestedSectionsProps> = ({
  suggestedSections,
}) => {
  return (
    <div className="suggestion-sections">
      {suggestedSections.map(
        (section: SuggestedChangeControlSection, index) => (
          <QBox style={{ marginBottom: '20px' }} key={section.title}>
            <QBox style={{ marginBottom: '10px' }}>
              <QHeading data-cy={`ai-generated-cc-section-heading-${index}`}>
                {section.title}
              </QHeading>
            </QBox>
            <QBox fontSize={'sm'}>
              {htmlToReactParser.parse(section.content)}
            </QBox>
          </QBox>
        ),
      )}
    </div>
  );
};

interface RationaleSectionProps {
  rationaleDetails: string[];
}

const RationaleSection = ({ rationaleDetails }: RationaleSectionProps) => {
  const { t } = useTranslation();
  const [panelOpen, setPanelOpen] = useState(false);
  return (
    <QBox mb="4">
      <QAlert
        data-cy="ai-generated-cc-content-rationale"
        title={t('labels.rationaleBehindSuggestions')}
        description={
          <QStack
            alignItems="flex-start"
            width="full"
            flexDirection="row"
            justifyContent="space-between"
          >
            <QCollapse isOpen={panelOpen} startingHeight={42}>
              <QStack
                alignItems="flex-start"
                width="full"
                flexDirection="row"
                justifyContent="space-between"
              >
                <QText casing="none" noOfLines={panelOpen ? undefined : 2}>
                  <QBox pl={5}>
                    <ul>
                      {rationaleDetails.map((rationaleDetail, index) => {
                        return <li key={index}>{rationaleDetail}</li>;
                      })}
                    </ul>
                  </QBox>
                </QText>

                <QIconButton
                  iconName={panelOpen ? 'ChevronUp' : 'ChevronDown'}
                  size="xs"
                  aria-label="Open"
                  onClick={() => setPanelOpen((state) => !state)}
                  data-cy="toggle-rationale-alert-size"
                />
              </QStack>
            </QCollapse>
          </QStack>
        }
        status="info"
      />
    </QBox>
  );
};

export const GenerateCCComponent = ({
  changeControl,
  editors,
}: {
  changeControl: ChangeControlDTO;
  editors: Editor[];
}) => {
  const { qualioDocument } = useContext(DocumentOverviewContext);

  if (!qualioDocument || !changeControl || qualioDocument.major_version < 1) {
    return <></>;
  }

  return (
    <GenerateCCBox
      document={qualioDocument}
      currentCC={changeControl}
      editors={editors}
    />
  );
};

const GenerateCCBox: React.FC<{
  document: QualioDocument;
  currentCC: ChangeControlDTO;
  editors: Editor[];
}> = ({ document, currentCC, editors }) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const [isFetching, setIsFetching] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [rationaleDetails, setRationaleDetails] = useState<string[]>([]);
  const [suggestedSections, setSuggestedSections] = useState<
    SuggestedChangeControlSection[] | null
  >(null);
  const [acceptedCurrentSuggestion, setAcceptedCurrentSuggestion] =
    useState(false);
  const shouldUseMultiRootEditor =
    document.editor_version?.includes('multiroot');
  const handleAccept = (
    suggestedSections: SuggestedChangeControlSection[] | null,
  ) => {
    if (!suggestedSections) {
      return;
    }
    if (shouldUseMultiRootEditor) {
      const sortedRoots = editors[0].model.document
        .getRoots()
        .sort((a, b) => {
          const orderA = Number(a.getAttribute('order') ?? 0);
          const orderB = Number(b.getAttribute('order') ?? 0);
          return orderA - orderB;
        })
        .filter((root) => !root.getAttribute('isHeader'));
      for (let i = 0; i < suggestedSections.length; i++) {
        const suggestedSection = suggestedSections[i];
        const sectionContent = suggestedSection.content;
        if (sortedRoots[i]) {
          editors[0].data.set(
            {
              [sortedRoots[i].rootName]: sectionContent,
            },
            {
              suppressErrorInCollaboration: true,
              batchType: {
                isUndoable: true,
              },
            },
          );
        }
        setAcceptedCurrentSuggestion(true);
      }
    } else {
      for (let i = 0; i < suggestedSections.length; i++) {
        const suggestedSection = suggestedSections[i];
        const sectionContent = suggestedSection.content;
        editors[i].data.set(sectionContent, {
          suppressErrorInCollaboration: true,
        });
        setAcceptedCurrentSuggestion(true);
      }
    }

    showToast({
      'data-cy': 'generate-cc-accept-success-toast',
      id: 'accept-ai-change-control-success',
      status: 'success',
      title: t('notifications.successfullyAppliedChangeControl'),
      description: t('messages.feelFreeToEditOrGenerate'),
      replace: true,
    });

    setIsPanelOpen(false);
  };

  const handleGenerate = async () => {
    setIsPanelOpen(true);
    let numAttempts = 0;
    while (numAttempts < 3) {
      try {
        setIsFetching(true);
        const generatedCCText = await aiService.generateChangeControl(
          document,
          currentCC,
        );

        const rationaleItems = generatedCCText.rationale;
        if (rationaleItems.length === 0) {
          throw new Error('Failed to parse generated change control rationale');
        }
        setRationaleDetails(generatedCCText.rationale);

        const sections = generatedCCText.changeControl;
        if (Object.keys(sections).length === 0) {
          throw new Error(
            'Failed to parse generated change control suggested sections',
          );
        }
        setSuggestedSections(sections);
        setAcceptedCurrentSuggestion(false);
        setIsFetching(false);

        break;
      } catch (e: any) {
        console.warn(e);

        // PS-238: the backend returns 422 when the document content is junk /
        // placeholder / blank. This is a deterministic refusal — do not retry,
        // and tell the user there isn't enough content to generate from.
        if (e?.response?.status === 422) {
          setIsFetching(false);
          // Close the panel so the loading spinner stops instead of being left
          // spinning on "typing suggestions" with no suggestions to show.
          setIsPanelOpen(false);
          // Clear any prior suggestions so a stale (now-invalid) suggestion
          // cannot be viewed or accepted after the document became junk.
          setSuggestedSections(null);
          setRationaleDetails([]);
          setAcceptedCurrentSuggestion(false);
          showToast({
            'data-cy': 'generate-cc-insufficient-content-toast',
            id: 'generate-ai-change-control-insufficient-content',
            status: 'warning',
            title: t('notifications.cannotGenerateChangeControl'),
            description:
              e?.response?.data?.reason ??
              t('errors.insufficientDocumentContent'),
            replace: true,
          });
          break;
        }

        // PS-235: the backend returns 413 (CONTEXT_WINDOW_EXCEEDED) when the
        // document content is too large to fit the model's context window. This
        // is also a deterministic refusal — do not retry, and close the panel so
        // the loading spinner stops instead of being left spinning.
        if (e?.response?.status === 413) {
          setIsFetching(false);
          setIsPanelOpen(false);
          setSuggestedSections(null);
          setRationaleDetails([]);
          setAcceptedCurrentSuggestion(false);
          showToast({
            'data-cy': 'generate-cc-content-too-large-toast',
            id: 'generate-ai-change-control-content-too-large',
            status: 'warning',
            title: t('notifications.cannotGenerateChangeControl'),
            description:
              e?.response?.data?.message ??
              t('errors.documentTooLargeToGenerate'),
            replace: true,
          });
          break;
        }

        // The backend returns 503 (MODEL_UNAVAILABLE) when Bedrock is
        // transiently out of capacity. Retrying immediately in a tight loop
        // won't help (the Retry-After is ~10s), so break out, close the panel,
        // and tell the user AI is temporarily unavailable. The banner's
        // Generate button lets them try again when they choose.
        if (
          e?.response?.status === 503 ||
          e?.response?.data?.code === 'MODEL_UNAVAILABLE'
        ) {
          setIsFetching(false);
          setIsPanelOpen(false);
          setSuggestedSections(null);
          setRationaleDetails([]);
          setAcceptedCurrentSuggestion(false);
          showToast({
            'data-cy': 'generate-cc-service-unavailable-toast',
            id: 'generate-ai-change-control-service-unavailable',
            status: 'warning',
            title: t('notifications.cannotGenerateChangeControl'),
            description:
              e?.response?.data?.message ??
              t('errors.aiTemporarilyUnavailable'),
            replace: true,
          });
          break;
        }

        numAttempts++;
      }

      if (numAttempts >= 3) {
        setIsFetching(false);
        showToast({
          'data-cy': 'generate-cc-generate-error-toast',
          id: 'generate-ai-change-control-error',
          status: 'error',
          title: t('notifications.cannotGenerateChangeControl'),
          description: t('errors.failedToCreateChangeControlSuggestions'),
          replace: true,
        });
      }
    }
  };

  return (
    <>
      {isPanelOpen && (
        <legacy.QDrawer
          size="lg"
          isOpen={true}
          onClose={() => {
            setIsPanelOpen(false);
          }}
        >
          <legacy.QDrawerHeader>
            <QText>{t('labels.qualioAi')}</QText>

            <QStack direction="row">
              <QButton
                variant="outline"
                onClick={handleGenerate}
                isDisabled={isFetching}
                data-cy="regenerate-ai-change-control-drawer"
                leftIcon="QualioAI"
              >
                {t('buttons.regenerate')}
              </QButton>
              <QButton
                data-cy="accept-ai-change-control"
                onClick={() => handleAccept(suggestedSections)}
                isDisabled={isFetching || acceptedCurrentSuggestion}
              >
                {t('buttons.accept')}
              </QButton>
            </QStack>
            <QCloseButton onClick={() => setIsPanelOpen(false)} />
          </legacy.QDrawerHeader>

          {isFetching || !suggestedSections ? (
            <QStack
              height="100%"
              minHeight="60vh"
              justify="center"
              align="center"
            >
              <QSpinner />
              <QText data-cy="generate-suggestions" fontSize={'sm'}>
                {t('labels.typingSuggestions')}
              </QText>
            </QStack>
          ) : (
            <>
              <QBox
                style={{ marginTop: '-10px', marginBottom: '10px' }}
                data-cy="ai-cc-disclaimer"
              >
                <QText fontSize="sm">
                  {t('messages.reviewGeneratedContentCarefully')}
                </QText>
              </QBox>
              <RationaleSection rationaleDetails={rationaleDetails} />
              <SuggestedSections suggestedSections={suggestedSections} />
            </>
          )}
        </legacy.QDrawer>
      )}
      <QBox
        width="full"
        borderRadius="md"
        padding="16px 24px"
        mb={6}
        background="linear-gradient(90.14deg, #3D4CF2 0%, #7B4FFF 100%)"
        data-cy="generate-banner"
        className={styles['generate-cc-component']}
      >
        <QStack
          justify="space-between"
          alignContent="center"
          alignItems="center"
          direction="row"
        >
          <QStack direction="column">
            <QText weight="semibold" color="white" fontSize="md">
              {t('labels.needSuggestions')}
            </QText>
            <QText fontSize="xs" color="white">
              {t('comments.useQualioAiToCompareVersions')}
            </QText>
          </QStack>
          {suggestedSections !== null ? (
            <QStack direction="row">
              <QButton
                data-cy="regenerate-ai-change-control-banner"
                onClick={handleGenerate}
                isDisabled={isFetching}
                leftIcon="QualioAI"
                variant="outline"
              >
                {t('buttons.regenerate')}
              </QButton>
              <QButton
                data-cy="view-suggestions-ai-change-control-banner"
                variant="outline"
                onClick={() => setIsPanelOpen(true)}
                isDisabled={isFetching}
              >
                {t('buttons.viewSuggestions')}
              </QButton>
            </QStack>
          ) : (
            <QButton
              variant="outline"
              leftIcon={'QualioAI' as any}
              onClick={handleGenerate}
              isDisabled={isFetching}
              data-cy="generate-ai-cc"
            >
              {t('buttons.generate')}
            </QButton>
          )}
        </QStack>
      </QBox>
    </>
  );
};
